/* ==========================================================================
   LADDOM — MOTION ENGINE (Module 04)
   Singleton window.Motion. No section registers its own ScrollTrigger.
   Reduced-motion is evaluated FIRST and re-evaluated live.
   ========================================================================== */
(function () {
  'use strict';

  // Every element the reveal engine drives. Kept in one place so a new
  // contract (the media settle) cannot be added to the observer and forgotten
  // in the reduced-motion fallbacks, which would leave content invisible.
  var REVEAL_SEL = '[data-reveal], [data-reveal-clip], [data-reveal-media]';

  var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  var coarse = window.matchMedia('(pointer: coarse)');
  var hasGsap = typeof window.gsap !== 'undefined';
  var hasST = hasGsap && typeof window.ScrollTrigger !== 'undefined';
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  // root -> { observers: [], triggers: [] }
  var owned = new Map();

  function bucket(root) {
    if (!owned.has(root)) owned.set(root, { observers: [], triggers: [] });
    return owned.get(root);
  }

  var Motion = {
    reduced: mq.matches,
    lenis: null,
    initialised: false,
    _queue: [],
    _refreshTimer: null,
  };

  /* ---------------------------------------------------------------- REVEAL
     IntersectionObserver, never ScrollTrigger. This is the workhorse and it
     must be nearly free. Unobserves after firing so nothing re-animates. */
  Motion.reveal = function (root) {
    root = root || document;
    var nodes = root.querySelectorAll(REVEAL_SEL);
    if (!nodes.length) return;

    if (Motion.reduced) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    nodes.forEach(function (n) {
      if (n.getAttribute('data-reveal') === 'stagger') {
        // Cap accumulation at 6 — beyond that the last child feels late.
        Array.prototype.slice.call(n.children).forEach(function (child, i) {
          child.style.setProperty('--i', Math.min(i, 5));
        });
      }
      io.observe(n);
    });

    bucket(root).observers.push(io);
  };

  /* -------------------------------------------------------------- PARALLAX
     Transform only. Never background-position. Desktop only. */
  Motion.parallax = function (root) {
    root = root || document;
    if (Motion.reduced || !hasST || window.innerWidth < 768) return;

    root.querySelectorAll('[data-parallax]').forEach(function (el) {
      var amount = parseFloat(el.getAttribute('data-parallax')) || 0.15;
      if (amount > 0.25) amount = 0.25; // past this the image edges reveal
      var t = window.gsap.to(el, {
        yPercent: amount * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6, // the slight lag is what reads as weight
        },
      });
      if (t.scrollTrigger) bucket(root).triggers.push(t.scrollTrigger);
    });
  };

  Motion.pin = function (el, opts) {
    if (Motion.reduced || !hasST || window.innerWidth < 990) return null;
    var st = window.ScrollTrigger.create(
      Object.assign({ trigger: el, pin: true, pinSpacing: true }, opts || {})
    );
    bucket(document).triggers.push(st);
    return st;
  };

  Motion.refresh = function () {
    if (!hasST) return;
    clearTimeout(Motion._refreshTimer);
    Motion._refreshTimer = setTimeout(function () {
      window.ScrollTrigger.refresh();
    }, 100);
  };

  Motion.destroy = function (root) {
    root = root || document;
    var b = owned.get(root);
    if (!b) return;
    b.observers.forEach(function (o) { o.disconnect(); });
    b.triggers.forEach(function (t) { t.kill(); });
    owned.delete(root);
  };

  Motion.register = function (fn) {
    Motion._queue.push(fn);
    if (Motion.initialised) fn(document);
  };

  /* ----------------------------------------------------------------- LENIS
     Only when: not reduced, not touch-primary, not in the theme editor.
     Driven from GSAP's ticker — NOT its own RAF loop. Without this the
     scroll-triggered animation desyncs from the smooth scroll. */
  function initLenis() {
    if (Motion.reduced || coarse.matches || designMode) return;
    if (typeof window.Lenis === 'undefined' || !hasGsap) return;

    Motion.lenis = new window.Lenis({
      lerp: 0.085,
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false,
    });

    if (hasST) Motion.lenis.on('scroll', window.ScrollTrigger.update);
    window.gsap.ticker.add(function (t) { Motion.lenis.raf(t * 1000); });
    window.gsap.ticker.lagSmoothing(0);
  }

  function teardownLenis() {
    if (!Motion.lenis) return;
    Motion.lenis.destroy();
    Motion.lenis = null;
  }

  /* ------------------------------------------------------------------ INIT */
  function init() {
    if (!Motion.reduced) initLenis();
    Motion.reveal(document);
    Motion.parallax(document);
    Motion._queue.forEach(function (fn) { fn(document); });
    Motion.initialised = true;
  }

  // Live reduced-motion changes. Never leave a reduce-motion user with
  // invisible content.
  function onReducedChange() {
    Motion.reduced = mq.matches;
    if (Motion.reduced) {
      teardownLenis();
      document
        .querySelectorAll(REVEAL_SEL)
        .forEach(function (n) { n.classList.add('is-in'); });
      owned.forEach(function (b) { b.triggers.forEach(function (t) { t.kill(); }); });
    } else {
      initLenis();
      Motion.parallax(document);
      Motion.refresh();
    }
  }
  if (mq.addEventListener) mq.addEventListener('change', onReducedChange);
  else if (mq.addListener) mq.addListener(onReducedChange);

  /* --------------------------------------------------- THEME EDITOR SURVIVAL
     Mandatory. Without this the merchant cannot edit the theme. */
  document.addEventListener('shopify:section:load', function (e) {
    Motion.destroy(e.target);
    Motion.reveal(e.target);
    Motion.parallax(e.target);
    Motion._queue.forEach(function (fn) { fn(e.target); });
    Motion.refresh();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    Motion.destroy(e.target);
    Motion.refresh();
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (Motion.lenis) Motion.lenis.scrollTo(e.target, { offset: -120 });
    else e.target.scrollIntoView({ block: 'center' });
  });

  window.Motion = Motion;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
